import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { Link } from 'react-router';
import { get } from 'lodash';
import { Typography } from '@mui/material';
import { createOrganization, getOrganization, updateOrganization } from './_api';
import { IOrganizationForm } from './_types';
import OrganizationFormComponent from './OrganizationFormComponent';
import Button from 'components/Buttons/Button';
import { useAppGlobals } from 'utils/hooks/useAppGlobals';
import useAlerts from 'components/Alerts/useAlerts';
import { Papeer } from 'components/Papeer/Papeer';
import { useAppInfo } from 'utils/hooks/useAppInfo';
import { useWithEntityTitle } from 'utils/hooks/useWithEntityTitle';

const linkBack = '/administration/patientRegistry?tab=organizations';

const OrganizationForm: React.FC = () => {
  const { t } = useTranslation('Organizations');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toggleLoader } = useAppGlobals();
  const { addSuccessAlert, addErrorAlert } = useAlerts();
  const { compactMode } = useAppInfo();

  const isCreating = id === 'create';
  const [entity, setEntity] = useState<any>(null);
  const [isFetching, setIsFetching] = useState<boolean>(true);

  const [defaultValues, setDefaultValues] = useState<IOrganizationForm>({
    name: '',
    active: true,
    identifier: [],
    telecom: [],
    address: [],
    mdexNodeUuid: null,
  });

  const { title } = useWithEntityTitle(isCreating, entity, t, 'createTitle', 'editTitle');

  const loadEntity = async () => {
    toggleLoader();

    if (!isCreating && id) {
      try {
        const organizationData = await getOrganization(parseInt(id));
        if (organizationData) {
          setEntity(organizationData);

          const defaultValues = {
            id: organizationData.id,
            name: get(organizationData, 'name', ''),
            active: get(organizationData, 'active', true),
            identifier: get(organizationData, 'identifier', []),
            telecom: get(organizationData, 'telecom', []),
            address: get(organizationData, 'address', []),
            mdexNodeUuid: get(organizationData, 'mdexNode.uuid', null),
          };
          setDefaultValues(defaultValues);
        }
      } catch (error) {
        addErrorAlert(t('loadError'));
        console.error('Error loading organization:', error);
      }
    }

    setIsFetching(false);
    toggleLoader(false);
  };

  const handleSubmit = async (data: any) => {
    toggleLoader();
    try {
      const payload = {
        name: data.name,
        active: data.active ?? true,
        identifier: data.identifier || [],
        telecom: data.telecom || [],
        address: data.address || [],
        mdexNodeUuid: data.mdexNodeUuid ?? null,
      };

      if (isCreating) {
        await createOrganization(payload);
        addSuccessAlert(t('createSuccess'));
      } else {
        await updateOrganization({ ...payload, id: parseInt(id!) });
        addSuccessAlert(t('updateSuccess'));
      }
      navigate(linkBack);
    } catch (error: any) {
      const errorMessage = isCreating ? t('createError') : t('updateError');
      addErrorAlert(errorMessage);
      console.error('Error saving organization:', error);
    } finally {
      toggleLoader(false);
    }
  };

  useEffect(() => {
    loadEntity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (isFetching) {
    return null;
  }

  if (!isCreating && !entity) {
    return (
      <>
        <Papeer>
          <Typography>{t('notFound')}</Typography>

          <Button
            variant="contained"
            size="large"
            component={Link}
            sx={{ mr: 1, mt: 2 }}
            {...({ to: linkBack } as any)}
          >
            {t('back')}
          </Button>
        </Papeer>
      </>
    );
  }

  return (
    <OrganizationFormComponent
      isCreating={isCreating}
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      linkBack={linkBack}
      compactMode={compactMode}
      title={title}
    />
  );
};

export default OrganizationForm;
